// Admin error envelope mapper. Mirrors A.1.1's shape with admin-specific
// copy. Per spec §6: "The shell renders the user-facing message verbatim
// from error.message per auth-onboarding.md §11" — so for codes without a
// hand-curated mapping we surface the upstream message directly via toast.

import { SUPPORT_EMAIL } from './constants';

export interface ErrorEnvelope {
  code: string;
  message?: string;
  details?: { fieldErrors?: Record<string, string[]> };
}

export interface ErrorBody {
  error?: ErrorEnvelope;
}

export interface MappedError {
  inline?: { field?: string; message: string };
  toast?: { message: string };
  redirect?: string;
}

export function mapAuthError(body: ErrorBody | unknown, status: number): MappedError {
  const code = extractCode(body) ?? `auth.http_${status}`;
  const upstreamMessage = extractMessage(body);

  switch (code) {
    case 'auth.invalid_email':
      return { inline: { field: 'email', message: 'Enter a valid email address.' } };
    case 'auth.rate_limited':
      return { toast: { message: 'Too many attempts. Try again in a few minutes.' } };
    case 'auth.invalid_code':
      return { inline: { field: 'code', message: 'That code didn’t match. Try again.' } };
    case 'auth.code_expired':
      return { inline: { field: 'code', message: 'That code expired. Request a new one.' } };
    case 'auth.too_many_attempts':
      return { toast: { message: 'Too many incorrect tries. Request a new code to continue.' } };
    case 'auth.totp_invalid':
      return { inline: { field: 'code', message: 'That code didn’t match. Check your authenticator and try again.' } };
    case 'auth.backup_code_invalid':
      return { inline: { field: 'code', message: 'That backup code didn’t match. Try a different one.' } };
    case 'auth.totp_locked_out':
      return {
        toast: {
          message: `Too many failed codes. Contact an ORG_ADMIN at ${SUPPORT_EMAIL} to unlock your account.`,
        },
      };
    case 'auth.totp_not_enrolled':
      return { redirect: '/settings/security/enroll-mfa' };
    case 'auth.expired':
      return { redirect: '/signin?reason=expired' };
    case 'auth.revoked_session':
      return { redirect: '/signin?reason=expired' };
    case 'auth.revoked_global':
    case 'auth.token_version_changed':
      return { toast: { message: 'Your session was ended by an administrator.' }, redirect: '/signin' };
    case 'auth.wrong_pool':
      return { toast: { message: 'Sign in with your admin account, not a learner account.' }, redirect: '/signin?reason=wrong_pool' };
    // Phase 4 (BLD.SPEC.12 PHASE 4 AMENDMENT): Cognito UserNotConfirmedException.
    // Recoverable — the account exists but the email is unconfirmed. Surface a
    // clear, actionable message instead of falling through to the generic crash.
    case 'auth.user_not_confirmed':
      return {
        inline: {
          field: 'email',
          message: 'Your email address has not been confirmed. Check your inbox for the confirmation email, then sign in again.',
        },
      };
    case 'auth.validation_failed': {
      const fieldErrors = extractFieldErrors(body);
      const first = fieldErrors ? Object.entries(fieldErrors)[0] : undefined;
      if (first) {
        return { inline: { field: first[0], message: first[1][0] ?? 'Invalid value.' } };
      }
      return { toast: { message: 'Some fields are invalid. Please correct and resubmit.' } };
    }
    default:
      return { toast: { message: upstreamMessage ?? 'Something went wrong. Please try again.' } };
  }
}

function extractCode(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const env = (body as ErrorBody).error;
  return env?.code;
}

function extractMessage(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const env = (body as ErrorBody).error;
  return env?.message;
}

function extractFieldErrors(body: unknown): Record<string, string[]> | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const env = (body as ErrorBody).error;
  return env?.details?.fieldErrors;
}
